"use client";

import * as React from "react";
import { flushSync } from "react-dom";
import { Sun, Moon } from "@phosphor-icons/react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

export function ThemeToggle() {
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="w-12 h-12 rounded-full bg-zinc-100 animate-pulse" />
    );
  }

  const isDark = resolvedTheme === "dark";

  const toggleTheme = async () => {
    if (isAnimating) return;

    const nextTheme = isDark ? "light" : "dark";
    const isMobile = window.innerWidth < 768;

    // Mobile: simple instant toggle with body class transition
    if (isMobile || !document.startViewTransition) {
      setTheme(nextTheme);
      return;
    }

    // Desktop: View Transitions API with curtain effect
    setIsAnimating(true);

    try {
      document.documentElement.classList.add("theme-transitioning");

      const transition = document.startViewTransition(() => {
        // flushSync forces React to update DOM synchronously
        // This is REQUIRED for View Transitions API to capture the new state
        flushSync(() => {
          setTheme(nextTheme);
        });
      });

      await transition.finished;
    } catch {
      // Fallback: if View Transition fails, just set theme normally
      setTheme(nextTheme);
    } finally {
      document.documentElement.classList.remove("theme-transitioning");
      setIsAnimating(false);
    }
  };

  return (
    <button
      onClick={toggleTheme}
      disabled={isAnimating}
      className={cn(
        "relative w-12 h-12 rounded-full transition-all duration-500 group overflow-hidden",
        "border-2 flex items-center justify-center",
        isDark
          ? "bg-zinc-900 border-zinc-700 hover:border-brand-gold hover:bg-zinc-800"
          : "bg-amber-50 border-amber-200 hover:border-brand-gold hover:bg-amber-100",
        isAnimating && "scale-95"
      )}
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
    >
      {/* Sun icon */}
      <Sun
        weight="fill"
        className={cn(
          "absolute w-6 h-6 transition-all duration-500",
          isDark
            ? "opacity-0 rotate-90 scale-0 text-amber-400"
            : "opacity-100 rotate-0 scale-100 text-amber-500 group-hover:text-brand-gold"
        )}
      />

      {/* Moon icon */}
      <Moon
        weight="fill"
        className={cn(
          "absolute w-5 h-5 transition-all duration-500",
          isDark
            ? "opacity-100 rotate-0 scale-100 text-brand-gold group-hover:text-amber-300"
            : "opacity-0 -rotate-90 scale-0 text-zinc-400"
        )}
      />

      {/* Glow effect */}
      <div
        className={cn(
          "absolute inset-0 rounded-full transition-opacity duration-500",
          isDark
            ? "bg-gradient-to-tr from-brand-gold/20 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100"
            : "bg-gradient-to-tr from-amber-300/30 via-transparent to-orange-300/20 opacity-0 group-hover:opacity-100"
        )}
      />

      {/* Animated ring on hover */}
      <div
        className={cn(
          "absolute inset-0 rounded-full border-2 scale-100 opacity-0 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300",
          isDark ? "border-brand-gold/30" : "border-amber-400/40"
        )}
      />
    </button>
  );
}
